/**
 * Programmatic SEO — Page « Facturation électronique à [Ville] »
 * Generates a unique, indexable page for each French city.
 * Target keyword: "logiciel facturation électronique [ville]"
 */
import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check, MapPin, Sparkles, Shield, Zap } from 'lucide-react';
import cities from '../../data/seo/cities.json';
import secteurs from '../../data/seo/secteurs.json';

export default function CityPage() {
  const { slug } = useParams();
  const city = cities.find((c) => c.slug === slug);

  if (!city) {
    return <Navigate to="/" replace />;
  }

  // Pick 4 sister cities (same region preferred, fallback random)
  const sisters = [
    ...cities.filter((c) => c.region === city.region && c.slug !== city.slug),
    ...cities.filter((c) => c.region !== city.region && c.slug !== city.slug),
  ].slice(0, 4);

  // Pick 4 sectors
  const featuredSecteurs = secteurs.slice(0, 4);

  const title = `Logiciel de facturation électronique à ${city.name} | SmartFacture AI 2026`;
  const description = `Logiciel de facturation électronique conforme 2026 pour les ${city.businesses.toLocaleString('fr-FR')} entreprises de ${city.name} (${city.dept}). Factur-X, scanner IA, simulation PPF/PDP. Essai gratuit.`;
  const url = `https://smartfacture-ai.com/facturation-electronique/${city.slug}`;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://smartfacture-ai.com/' },
      { '@type': 'ListItem', position: 2, name: 'Facturation électronique', item: 'https://smartfacture-ai.com/facturation-electronique' },
      { '@type': 'ListItem', position: 3, name: city.name, item: url },
    ],
  };

  return (
    <div className="min-h-screen bg-[#FAFFF7]" data-testid="seo-city-page">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={url} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={url} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Breadcrumbs */}
      <nav className="max-w-5xl mx-auto px-6 pt-8 pb-2 text-xs text-slate-500" aria-label="Fil d'Ariane">
        <Link to="/" className="hover:underline">Accueil</Link>
        <span className="mx-1.5">›</span>
        <span className="text-slate-700">Facturation électronique</span>
        <span className="mx-1.5">›</span>
        <span className="text-slate-900 font-medium">{city.name}</span>
      </nav>

      {/* Hero */}
      <section className="max-w-5xl mx-auto px-6 pt-8 pb-16">
        <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1 rounded-full text-xs font-semibold mb-5">
          <MapPin className="h-3.5 w-3.5" />
          {city.region} · Département {city.dept}
        </div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-5">
          Logiciel de facturation électronique
          <br />
          à <span className="text-emerald-600">{city.name}</span>
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl mb-7">
          {city.blurb} SmartFacture AI accompagne les <strong className="text-slate-900">{city.businesses.toLocaleString('fr-FR')} entreprises</strong> de {city.name} dans la conformité à la réforme de la facturation électronique du 1er septembre 2026.
        </p>
        <div className="flex flex-wrap gap-3">
          <Link to="/register" className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-6 py-3 rounded-xl shadow-lg shadow-emerald-500/30 transition-all hover:-translate-y-0.5" data-testid="seo-city-cta-register">
            Essayer gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/pricing" className="inline-flex items-center gap-2 bg-white text-slate-700 font-semibold px-6 py-3 rounded-xl border border-slate-200 hover:border-emerald-300 transition">
            Voir les tarifs
          </Link>
        </div>
      </section>

      {/* Why SmartFacture for [city] */}
      <section className="bg-white py-16 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Pourquoi les entreprises de {city.name} choisissent SmartFacture AI
          </h2>
          <p className="text-slate-600 mb-10 max-w-2xl">
            La réforme de la facturation électronique impose à toutes les entreprises françaises — y compris les freelances et TPE de {city.name} — d'émettre et recevoir des factures au format Factur-X via le PPF ou une PDP agréée à partir du 1er septembre 2026.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: 'Conformité 2026 garantie', desc: `Factur-X (PDF/A-3 + XML), conforme à la norme française EN16931 et au cadre PPF/PDP. Vos factures sont prêtes pour les obligations qui touchent les entreprises de ${city.name}.` },
              { icon: Sparkles, title: 'Copilote IA en français', desc: `Tapez « Facture Dupont 3 jours à 800€ » dans le Copilote IA — votre facture est créée en 5 secondes. Premier outil français à proposer cette fonctionnalité.` },
              { icon: Zap, title: 'Scanner IA OCR', desc: `Photographiez vos reçus et factures fournisseurs : l'IA extrait automatiquement montant, date, TVA. Gain de temps massif pour les TPE de ${city.region}.` },
            ].map((f) => (
              <div key={f.title} className="bg-emerald-50/50 border border-emerald-100 rounded-2xl p-6">
                <div className="h-11 w-11 rounded-xl bg-emerald-500 flex items-center justify-center mb-4 shadow-md shadow-emerald-500/20">
                  <f.icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-2">{f.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local context */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-3xl font-bold text-slate-900 mb-3">
          La facturation électronique à {city.name} : ce qui change en 2026
        </h2>
        <div className="prose prose-slate max-w-3xl space-y-4 text-slate-700 leading-relaxed">
          <p>
            À {city.name} comme partout en France, toutes les entreprises assujetties à la TVA doivent <strong>recevoir</strong> des factures électroniques au format Factur-X dès le <strong>1er septembre 2026</strong>. L'<strong>émission</strong> devient ensuite obligatoire selon une calendrier progressif :
          </p>
          <ul className="space-y-2">
            <li className="flex gap-2"><Check className="h-5 w-5 text-emerald-500 flex-shrink-0" /> <span><strong>1er septembre 2026</strong> : grandes entreprises et ETI émettent en Factur-X</span></li>
            <li className="flex gap-2"><Check className="h-5 w-5 text-emerald-500 flex-shrink-0" /> <span><strong>1er septembre 2027</strong> : TPE/PME émettent en Factur-X (la majorité des entreprises de {city.name})</span></li>
            <li className="flex gap-2"><Check className="h-5 w-5 text-emerald-500 flex-shrink-0" /> <span>Toutes les factures transitent par le <strong>PPF</strong> (Portail Public de Facturation) ou une <strong>PDP</strong> agréée par la DGFiP</span></li>
          </ul>
          <p>
            SmartFacture AI génère vos factures directement au format Factur-X conforme, simule les dépôts PPF/PDP, et vous prépare à la transition sans stress. Que vous soyez freelance dans le {city.region.toLowerCase()}, TPE à {city.name} ou consultant indépendant, vous êtes prêt.
          </p>
        </div>
      </section>

      {/* Featured sectors */}
      <section className="bg-white py-16 border-y border-emerald-100">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            SmartFacture AI par secteur d'activité à {city.name}
          </h2>
          <p className="text-slate-600 mb-8 max-w-2xl">
            Chaque métier a ses spécificités fiscales et ses obligations. Découvrez la conformité Factur-X adaptée à votre secteur :
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            {featuredSecteurs.map((s) => (
              <Link
                key={s.slug}
                to={`/factur-x/${s.slug}`}
                className="group bg-emerald-50/50 hover:bg-emerald-50 border border-emerald-100 hover:border-emerald-300 rounded-xl p-5 transition"
                data-testid="seo-city-sector-link"
              >
                <h3 className="font-semibold text-slate-900 group-hover:text-emerald-700 transition">
                  Factur-X pour {s.name}
                </h3>
                <p className="text-sm text-slate-600 mt-1">{s.blurb}</p>
                <span className="inline-flex items-center gap-1 text-emerald-600 text-sm font-semibold mt-3 group-hover:gap-2 transition-all">
                  En savoir plus <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Sister cities (internal linking) */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">
          Facturation électronique dans d'autres villes de France
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {sisters.map((c) => (
            <Link
              key={c.slug}
              to={`/facturation-electronique/${c.slug}`}
              className="bg-white border border-slate-200 hover:border-emerald-300 rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:text-emerald-700 transition flex items-center justify-between group"
              data-testid="seo-sister-city-link"
            >
              <span>{c.name}</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-emerald-500 transition" />
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Bottom */}
      <section className="bg-gradient-to-br from-emerald-50 via-emerald-100 to-emerald-50 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-3">
            Prêt pour la conformité 2026 à {city.name} ?
          </h2>
          <p className="text-slate-700 mb-7">
            Essayez SmartFacture AI gratuitement, sans carte bancaire. Vous serez conforme en 5 minutes.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-7 py-3.5 rounded-xl shadow-xl shadow-emerald-500/30 transition-all hover:-translate-y-0.5"
            data-testid="seo-city-cta-bottom"
          >
            Commencer gratuitement <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-xs text-slate-500">
            Aucune carte bancaire · 5 factures gratuites/mois · Conformité Factur-X 2026
          </p>
        </div>
      </section>
    </div>
  );
}
